import styled, { css, keyframes } from 'styled-components'

const containerRotate = keyframes`
  to {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
`

const fillUnfillRotate = keyframes`
  12.5% {
    -webkit-transform: rotate(135deg);
    transform: rotate(135deg);
  }
  25% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  37.5% {
    -webkit-transform: rotate(405deg);
    transform: rotate(405deg);
  }
  50% {
    -webkit-transform: rotate(540deg);
    transform: rotate(540deg);
  }
  62.5% {
    -webkit-transform: rotate(675deg);
    transform: rotate(675deg);
  }
  75% {
    -webkit-transform: rotate(810deg);
    transform: rotate(810deg);
  }
  87.5% {
    -webkit-transform: rotate(945deg);
    transform: rotate(945deg);
  }
  to {
    -webkit-transform: rotate(1080deg);
    transform: rotate(1080deg);
  }
`

const leftSpin = keyframes`
  0% {
    -webkit-transform: rotate(130deg);
    transform: rotate(130deg);
  }
  50% {
    -webkit-transform: rotate(-5deg);
    transform: rotate(-5deg);
  }
  to {
    -webkit-transform: rotate(130deg);
    transform: rotate(130deg);
  }
`

const rightSpin = keyframes`
  0% {
    -webkit-transform: rotate(-130deg);
    transform: rotate(-130deg);
  }
  50% {
    -webkit-transform: rotate(5deg);
    transform: rotate(5deg);
  }
  to {
    -webkit-transform: rotate(-130deg);
    transform: rotate(-130deg);
  }
`

const line1FadeInOut = keyframes`
  0% {
    opacity: 1;
  }
  25% {
    opacity: 1;
  }
  26% {
    opacity: 0;
  }
  89% {
    opacity: 0;
  }
  90% {
    opacity: 1;
  }
  to {
    opacity: 1;
  }
`

const line2FadeInOut = keyframes`
  0% {
    opacity: 0;
  }
  15% {
    opacity: 0;
  }
  25% {
    opacity: 1;
  }
  50% {
    opacity: 1;
  }
  51% {
    opacity: 0;
  }
`

const line3FadeInOut = keyframes`
    0% {
    opacity: 0;
  }
  40% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  75% {
    opacity: 1;
  }
  76% {
    opacity: 0;
  }
`

const line4FadeInOut = keyframes`
  0% {
    opacity: 0;
  }
  65% {
    opacity: 0;
  }
  75% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`

const lineVariants = {
  1: css`
    border-color: #00b7af;
    animation: ${fillUnfillRotate} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite
        both,
      ${line1FadeInOut} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
  2: css`
    border-color: #2d559e;
    animation: ${fillUnfillRotate} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite
        both,
      ${line2FadeInOut} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
  3: css`
    border-color: #273f59;
    animation: ${fillUnfillRotate} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite
        both,
      ${line3FadeInOut} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
  4: css`
    border-color: #ccd1d8;
    animation: ${fillUnfillRotate} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite
        both,
      ${line4FadeInOut} 4800ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
} as const

const variants = {
  left: css`
    border-right-color: transparent;
    transform: rotate(129deg);
    animation: ${leftSpin} 1200ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
  right: css`
    left: -100%;
    border-left-color: transparent;
    transform: rotate(-129deg);
    animation: ${rightSpin} 1200ms cubic-bezier(0.4, 0, 0.2, 1) infinite both;
  `,
  center: css`
    width: 1000%;
    left: -450%;
  `,
} as const

export const Container = styled.div<{ $height?: string; $width?: string }>`
  height: ${({ $height }) => $height || 'auto'};
  width: ${({ $width }) => $width || '100%'};
  background-color: white;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  box-sizing: border-box;
  box-shadow: 2px 2px 1px rgba(0, 0, 0, 0.04);
  position: static;
  margin: 0;
`

export const Spinner = styled.div`
  height: 28px;
  left: 50%;
  margin-left: -14px;
  position: absolute;
  top: 45%;
  width: 28px;
  /* Spins external container */
  -webkit-animation: ${containerRotate} 1600ms linear infinite;
  animation: ${containerRotate} 1600ms linear infinite;
  position: static;
  margin: 0;
`

export const SpinnerLine = styled.div<{ variant: 1 | 2 | 3 | 4 }>`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;

  ${({ variant }) => lineVariants[variant]}
`

export const SpinnerLineCog = styled.div`
  display: inline-block;
  position: relative;
  width: 50%;
  height: 100%;
  overflow: hidden;
  border-color: inherit;
`

export const SpinnerLineCogInner = styled.div<{
  variant: 'left' | 'center' | 'right'
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  width: 200%;
  box-sizing: border-box;
  height: 100%;
  border-width: 3px;
  border-style: solid;
  border-color: inherit;
  border-bottom-color: transparent;
  border-radius: 50%;
  animation: none;

  ${({ variant }) => variants[variant]}
`

export const SpinnerLineTicker = styled.div`
  position: absolute;
  box-sizing: border-box;
  top: 0;
  left: 45%;
  width: 10%;
  height: 100%;
  overflow: hidden;
  border-color: inherit;
`
